'use client';

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { api, getApiErrorMessage } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { CircleSlash, Eye, EyeOff, RotateCw, TriangleAlert } from 'lucide-react';
import type { Drone } from '@/types/fleet';
import {
  RAIL_VERDICT_LABEL,
  batteryBarPercent,
  batteryTone,
  buildRailRows,
  formatBattery,
  formatEvaluatedAt,
  formatLinkAge,
  railRowAccessibleName,
  readinessRouteParam,
  type DraftWaypoint,
  type FleetReadiness,
  type RailRow,
  type StatusTone,
} from './missionCanvasShared';

/**
 * How long the route must sit still before the fleet is re-evaluated.
 *
 * The route changes on EVERY map click and on every frame of a marker drag; without this, drawing
 * a twelve-point orbit would fire a request per point and the operator would watch the rail
 * reorder under their hand. 400 ms is below the pause between two deliberate clicks, so a settled
 * route is judged immediately and a route being drawn is judged once, at the end.
 */
export const ROUTE_DEBOUNCE_MS = 400;

/**
 * Battery drains and links go stale while the canvas is open, so the verdict has a shelf life.
 * Rows are keyed by drone id, so a re-rank moves the DOM node and takes keyboard focus with it.
 */
export const READINESS_REFRESH_MS = 20_000;

const TONE_TEXT: Record<StatusTone, string> = {
  green: 'text-status-green-fg',
  amber: 'text-status-amber-fg',
  red: 'text-status-red-fg',
  gray: 'text-status-gray-fg',
};

const TONE_BAR: Record<StatusTone, string> = {
  green: 'bg-status-green-fg',
  amber: 'bg-status-amber-fg',
  red: 'bg-status-red-fg',
  gray: 'bg-status-gray-fg',
};

/**
 * Debounce a value, but let the FIRST one through untouched.
 *
 * A blanket delay would also postpone the initial fleet read, so the rail would sit empty for
 * 400 ms on open for no reason — there is nothing to coalesce yet.
 */
function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    if (Object.is(settled, value)) return;
    const timer = setTimeout(() => setSettled(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs, settled]);
  return settled;
}

interface ReadinessResult {
  payload: FleetReadiness;
  /** The exact route this verdict answers for, so a drifted route can be called out. */
  route: string | null;
}

export interface MissionReadinessRailProps {
  /** The roster the canvas already holds. Keeps every aircraft listed when readiness fails. */
  drones: Drone[];
  /** The roster itself failed to load — different from an org with no drones. */
  loadError: boolean;
  /** The route being drawn. Its containment is part of the verdict. */
  waypoints: DraftWaypoint[];
  /** Selected drone id, or null for the explicit Unassigned state. */
  value: string | null;
  onChange: (droneId: string | null) => void;
  /** Radio group name — unique per instance. */
  name?: string;
  /**
   * Locks every row while a save is in flight. Without it the rail was the one control in the
   * mission page's panel that could silently discard the operator's choice: select, Save, change
   * your mind mid-flight, and the refetch resets the draft back to what was saved. The selects
   * beside it were already guarded this way.
   */
  disabled?: boolean;
  /**
   * The mission's own record of its aircraft, used when that airframe is not in the roster —
   * deleted from the fleet, or the roster request failed. Without it no radio is checked at all
   * while the mission plainly has a drone, and an unchecked native radiogroup means the first
   * arrow key press silently changes the assignment.
   */
  currentAssignment?: { id: string; label: string } | null;
  /** Re-fetch the ROSTER (not the verdict). Null hides the offer. */
  onRetryRoster?: (() => void) | null;
  /**
   * Whether "Unassigned" is offered. False once the mission is committed: the API refuses to
   * clear crew on a scheduled or in-progress mission, so offering it would only produce a 400.
   */
  allowUnassign?: boolean;
  /**
   * Copy that differs between creating a mission and editing one. Defaults read for the canvas,
   * which is where this component started; the mission page passes its own, because a screen
   * reader announcing "create this mission without a drone" on a mission created days ago is
   * simply wrong.
   */
  copy?: {
    unassignedLabel?: string;
    noGoNote?: (callsign: string) => string;
    rosterFailed?: string;
    /**
     * The legend, when this pick is not simply "the drone that flies this mission".
     *
     * On compose's hold lane it is the FIRST aircraft of a rotation, and the rest are chosen after
     * the mission exists. The legend is the only thing on screen that can say so at the moment the
     * operator is looking at the picker, so it is copy and not a hardcoded word.
     */
    legend?: ReactNode;
    /** One line under the advisory, for whatever the legend could not carry. */
    note?: string;
  };
  /**
   * The currently-selected aircraft's verdict, reported up as it changes (including when a poll
   * re-evaluates it). Null when nothing is selected.
   *
   * Exists because the NOW lane must be able to REFUSE TO ARM on a NO-GO aircraft. Without it the
   * canvas had no idea what the rail was showing, so its launch strip rendered a green "Aircraft
   * and route ready" beside a red NO-GO row — telling the operator the opposite of what the rail
   * two inches above said.
   */
  onSelectedVerdict?: ((report: SelectedVerdictReport) => void) | null;
}

/** What the rail knows about the selected aircraft, reported up to the canvas. */
export interface SelectedVerdictReport {
  row: RailRow | null;
  /**
   * The verdict does not answer for the route as currently drawn: a request is in flight, the
   * route has moved on behind the debounce, or the last read failed. The canvas must not print
   * a green "ready" over this — a GO from before a waypoint was dragged into a keep-out area is
   * not a GO for what is about to be flown.
   */
  stale: boolean;
}

/**
 * The fleet picker, ranked by who can actually fly the drawn route right now.
 *
 * Replaces a dropdown of names. A dropdown makes the operator remember which airframe is charged,
 * which one is talking to us, and which one the keep-in fence allows over this route; the rail
 * answers all three in the row, ranked GO-first by the server.
 *
 * Two rules it will not break:
 *  - A NO-GO aircraft is HIDDEN BY DEFAULT — hidden, never withheld, and never silently.
 *    REVERSED 2026-08-23 on the founder's call (the IAF operator this product is benchmarked
 *    against), made with the old rule in view. That rule kept every NO-GO row on screen so the
 *    rail would teach the operator about their fleet; on a real fleet — fifteen airframes, most
 *    of them on charge, off link or outside the fence at any given moment — what it did instead
 *    was bury the two aircraft that can fly under thirteen that cannot, at the moment the
 *    operator is trying to launch one. The part of the old reasoning that survives is why they
 *    are REACHABLE rather than removed: a mission is a plan, and assigning an aircraft that is on
 *    charge today for a sortie next Tuesday is normal, so one control brings them all back,
 *    dimmed, carrying their reasons and selectable exactly as before. Three guards keep the
 *    hiding honest:
 *      * the control carries the COUNT ("Show 13 NO-GO aircraft"), so a hidden aircraft is never
 *        an aircraft the operator does not know exists — and when EVERY aircraft is NO-GO the
 *        rail says so in words rather than rendering an empty box;
 *      * only `no_go` hides. `unknown` never does — see the second rule;
 *      * the SELECTED aircraft is never hidden, even when the 20 s poll turns it NO-GO under the
 *        operator's hand. Taking their own choice out of the list is not a thing this may do.
 *    This holds on EVERY surface the rail is drawn on, not only the COP compose panel that asked
 *    for it: the mission-detail rail (`MissionSidebar`) picks from the same fleet with the same
 *    noise, and one picker that behaves two ways depending on which panel it sits in is a worse
 *    trade than one extra click on the surface that did not ask. Hence component state, not a
 *    prop — there is no call site whose answer differs.
 *  - A failed fetch is never rendered as "no drones". The roster still lists every aircraft, and
 *    every verdict reads UNKNOWN rather than borrowing GO from a request that did not answer.
 *    This is precisely why the rule above hides `no_go` ONLY: a failed readiness read turns EVERY
 *    row `unknown`, and a rail that hid those would answer a failed request with an empty fleet.
 *
 * The verdict is ADVISORY. It is a read of the fleet at `evaluated_at`, not permission: the server
 * re-runs these checks at launch and can refuse.
 */
export function MissionReadinessRail({
  drones, loadError, waypoints, value, onChange, name = 'canvas-drone',
  disabled = false, currentAssignment = null, onRetryRoster = null, allowUnassign = true, copy,
  onSelectedVerdict = null,
}: MissionReadinessRailProps) {
  const unassignedLabel = copy?.unassignedLabel ?? 'Unassigned. Create this mission without a drone.';
  const noGoNote = copy?.noGoNote
    ?? ((callsign: string) => `${callsign} is NO-GO right now. The mission is still created as a draft.`);
  const rosterFailed = copy?.rosterFailed ?? 'Fleet data could not be loaded — retry from the board';
  const [result, setResult] = useState<ReadinessResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** Bumped to force a re-read: the refresh timer and the operator's Retry both pull this. */
  const [reloadNonce, setReloadNonce] = useState(0);
  /**
   * The request that last came back. `loading` is DERIVED from the gap between this and what is
   * currently wanted, rather than being its own state flipped at the top of the effect — a
   * synchronous `setLoading(true)` in an effect body is a cascading render, and the two flags
   * could drift out of step on a superseded request.
   */
  const [answered, setAnswered] = useState<{ route: string | null; nonce: number } | null>(null);
  /**
   * Whether the NO-GO rows are on screen. Starts false — see the first rule at the top of this
   * file. Plain component state, deliberately: it is a way of LOOKING at the fleet right now, not
   * a setting, so it is forgotten when the panel closes. Persisting it would mean an operator who
   * expanded the rail once last week gets the noisy rail back on a launch they are in a hurry for,
   * with nothing on screen to explain why their rail looks different from the one beside them.
   */
  const [revealNoGo, setRevealNoGo] = useState(false);

  const routeParam = useMemo(() => readinessRouteParam(waypoints), [waypoints]);
  const settledRoute = useDebouncedValue(routeParam, ROUTE_DEBOUNCE_MS);
  const loading = answered === null || answered.route !== settledRoute || answered.nonce !== reloadNonce;

  useEffect(() => {
    const controller = new AbortController();
    const path = settledRoute === null
      ? '/api/drones/readiness'
      : `/api/drones/readiness?waypoints=${encodeURIComponent(settledRoute)}`;

    api.get<{ data: FleetReadiness }>(path, { signal: controller.signal })
      .then((res) => {
        if (controller.signal.aborted) return;
        setResult({ payload: res.data, route: settledRoute });
        setError(null);
      })
      .catch((err: unknown) => {
        // An abort is this effect being superseded, not a failure — reporting it would flash an
        // error banner on every click of a route being drawn.
        if (controller.signal.aborted) return;
        setError(getApiErrorMessage(err, 'Fleet readiness could not be evaluated'));
      })
      .finally(() => {
        if (controller.signal.aborted) return;
        setAnswered({ route: settledRoute, nonce: reloadNonce });
      });

    return () => controller.abort();
  }, [settledRoute, reloadNonce]);

  useEffect(() => {
    const timer = setInterval(() => setReloadNonce((n) => n + 1), READINESS_REFRESH_MS);
    return () => clearInterval(timer);
  }, []);

  const rows = useMemo(() => {
    const built = buildRailRows(drones, result?.payload.drones ?? null);
    // The assigned airframe is not in the roster: deleted from the fleet, or the roster request
    // failed. `withCurrent` did this for the dropdown this rail replaced; without it no radio is
    // checked while the mission plainly has a drone — and on a native radiogroup with nothing
    // checked, the first arrow key press silently changes the assignment.
    if (currentAssignment && !built.some((r) => r.droneId === currentAssignment.id)) {
      built.unshift({
        droneId: currentAssignment.id,
        callsign: currentAssignment.label,
        model: 'No longer in the fleet',
        verdict: 'unknown',
        reasons: [],
        batteryPercent: null,
        telemetryAgeSeconds: null,
      } as RailRow);
    }
    return built;
  }, [drones, result, currentAssignment]);

  const minBattery = result?.payload.min_battery_percent ?? 25;
  const goCount = rows.filter((r) => r.verdict === 'go').length;
  /** The drawn route has moved on from the one the verdict answers for. */
  const routeDrifted = result !== null && result.route !== routeParam;
  const firstLoad = result === null && loading;

  const selectedRow = value === null ? null : rows.find((r) => r.droneId === value) ?? null;

  /**
   * A row the rail is allowed to hide: NO-GO, and not the operator's own choice.
   *
   * The `value` half is not a nicety. The verdict re-polls every 20 s, so the aircraft that was GO
   * when it was picked can turn NO-GO while the panel sits open — and a picker that answers that
   * by deleting the checked row leaves a native radiogroup with nothing checked, which is both a
   * silent loss of the operator's decision and a group where the next arrow key press assigns
   * something else. It stays exactly where the server ranked it, dimmed, with its reasons and the
   * note under the rail; nothing about it moves.
   */
  const hideable = (row: RailRow) => row.verdict === 'no_go' && row.droneId !== value;
  const hideableCount = rows.filter(hideable).length;
  const visibleRows = revealNoGo ? rows : rows.filter((row) => !hideable(row));
  const hiddenCount = rows.length - visibleRows.length;
  /** Every aircraft this org has is NO-GO. An empty box that says nothing would be a lie by omission. */
  const allHidden = rows.length > 0 && visibleRows.length === 0;

  // Held in a ref so a caller that passes an inline arrow does not re-fire the effect on every
  // render — the reporting must track the VERDICT changing, not the parent re-rendering.
  const reportVerdict = useRef(onSelectedVerdict);
  // Kept current in its OWN effect rather than assigned during render: a ref write during render
  // is not safe under concurrent rendering. Declared first, so it has already run by the time the
  // reporting effect below fires on the same commit.
  useEffect(() => {
    reportVerdict.current = onSelectedVerdict;
  });
  // `error` counts as stale: a failed read tells us nothing about the current route.
  const verdictStale = loading || routeDrifted || error !== null;
  useEffect(() => {
    reportVerdict.current?.({ row: selectedRow, stale: verdictStale });
  }, [selectedRow, verdictStale]);
  // Counts the WHOLE fleet, then says how much of it is off screen — a summary that quietly
  // counted only the visible rows would make a hidden aircraft invisible twice over.
  const summary = result === null
    ? 'Fleet readiness not evaluated'
    : hiddenCount > 0
      ? `${rows.length} aircraft, ${goCount} GO, ${hiddenCount} NO-GO hidden`
      : `${rows.length} aircraft, ${goCount} GO`;

  return (
    <fieldset className="space-y-2 min-w-0">
      <legend className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-muted-foreground">
        {copy?.legend ?? (
          <>Drone <span className="lowercase tracking-normal opacity-70">optional</span></>
        )}
      </legend>

      {/* The verdict is a forecast, not clearance. Said in the rail rather than in a tooltip,
          because an operator who reads GO and skips the launch gate has been misled by us. */}
      <p className="text-[11px] leading-snug text-muted-foreground">
        <span className="uppercase tracking-wider text-status-amber-fg">Advisory</span>
        {' — '}
        readiness at a moment in time. The server re-checks at launch and can refuse.
      </p>

      {/* Whatever the legend could not carry. On the hold lane: that this pick is the FIRST of a
          rotation and the rest come after the mission exists - the one sentence that stops the
          coverage wizard's aircraft step reading as the same question asked twice. */}
      {copy?.note !== undefined && (
        <p className="text-[11px] leading-snug text-muted-foreground">{copy.note}</p>
      )}

      <div className="flex items-center justify-between gap-2 text-[10px] uppercase tracking-wider text-muted-foreground">
        <span className="tabular-nums truncate">
          {result === null
            ? 'Awaiting fleet'
            : `As of ${formatEvaluatedAt(result.payload.evaluated_at)} · ${rows.length} aircraft · ${goCount} go${hiddenCount > 0 ? ` · ${hiddenCount} hidden` : ''}`}
        </span>
        {(loading || routeDrifted) && (
          <span className="shrink-0 text-status-amber-fg">
            {loading ? 'Evaluating…' : 'Route changed'}
          </span>
        )}
      </div>

      {/* One announcement for the whole rail. Per-row live text would read the fleet out loud on
          every 20 s refresh. */}
      <p aria-live="polite" className="sr-only">{summary}</p>

      {error !== null && (
        <div className="flex items-start gap-1.5 rounded border border-status-amber-fg/30 bg-status-amber-fg/10 px-2 py-1.5 text-[11px] text-status-amber-fg">
          <TriangleAlert className="size-3.5 shrink-0 mt-0.5" strokeWidth={1.5} aria-hidden="true" />
          <span className="min-w-0 flex-1">
            {result === null
              ? `Readiness unavailable — ${error}. Every aircraft is still listed, with no verdict.`
              : `Refresh failed — ${error}. Showing the ${formatEvaluatedAt(result.payload.evaluated_at)} verdict.`}
          </span>
          <Button
            type="button" variant="ghost" size="sm"
            onClick={() => setReloadNonce((n) => n + 1)}
            className="h-6 shrink-0 px-1.5 text-[11px] text-status-amber-fg hover:bg-status-amber-fg/10"
          >
            <RotateCw className="size-3 mr-1" strokeWidth={1.5} aria-hidden="true" />
            Retry
          </Button>
        </div>
      )}

      {/* `relative` is load-bearing, not cosmetic. This is a SCROLL CONTAINER, and a scroll
          container that is not also a containing block does not contain the absolutely
          positioned boxes inside it: they escape its clip entirely and keep their static
          position in the un-scrolled flow. Every row's `sr-only` radio is such a box, so
          without this the fifteenth aircraft's input reported ~1800px down the panel and the
          browser's scroll-the-focused-element-into-view walked past this scroller to find
          something that could reach it. See `RailOption` for the rest of the story. */}
      <div className="relative space-y-1 max-h-80 overflow-y-auto overflow-x-hidden pr-0.5">
        {/* Unassigned is a real, first-class choice while the mission is still being planned: it
            can be created, and scheduled, with no aircraft. Once committed the API refuses to
            clear crew, so offering it there would only ever produce a 400. */}
        {allowUnassign && (
        <RailOption
          name={name}
          checked={value === null}
          onSelect={() => onChange(null)}
          ariaLabel={unassignedLabel}
          disabled={disabled}
        >
          <div className="flex items-center gap-2 min-w-0">
            <CircleSlash className="size-3.5 shrink-0 text-muted-foreground" strokeWidth={1.5} aria-hidden="true" />
            <span className="text-xs uppercase tracking-wider text-muted-foreground">Unassigned</span>
          </div>
        </RailOption>
        )}

        {firstLoad && rows.length === 0 && (
          <p className="px-2 py-3 text-[11px] text-muted-foreground">Evaluating the fleet…</p>
        )}

        {visibleRows.map((row) => (
          <RailOption
            key={row.droneId}
            name={name}
            checked={value === row.droneId}
            onSelect={() => onChange(row.droneId)}
            ariaLabel={railRowAccessibleName(row)}
            dimmed={row.verdict !== 'go'}
            disabled={disabled}
          >
            <RailRowBody row={row} minBattery={minBattery} />
          </RailOption>
        ))}

        {/* Not an empty rail. The fleet is there, it simply cannot fly — said in words, with the
            count, and with the control below still offering to show them. */}
        {allHidden && (
          <p className="px-2 py-3 text-[11px] leading-snug text-status-amber-fg">
            No aircraft are ready{waypoints.length > 0 ? ' for this route' : ''} — all {hiddenCount} are NO-GO and hidden.
          </p>
        )}

        {!firstLoad && rows.length === 0 && (
          <div className="px-2 py-3 space-y-1.5">
            <p className="text-[11px] text-muted-foreground">
              {loadError
                ? rosterFailed
                : error !== null
                  ? 'Readiness could not be read and no aircraft are cached — retry above'
                  : 'No drones yet — add one under Fleet'}
            </p>
            {/* The ROSTER, not the verdict — the Retry at the top of the rail only re-runs
                readiness. Without this the only recovery from a failed /api/drones was a full
                page reload. */}
            {loadError && onRetryRoster !== null && (
              <Button
                type="button" size="sm" variant="outline" onClick={onRetryRoster} disabled={disabled}
                className="h-7 border-border text-muted-foreground hover:bg-accent hover:text-foreground text-[11px]"
              >
                <RotateCw className="size-3 mr-1.5" strokeWidth={1.5} aria-hidden="true" />
                Reload the fleet
              </Button>
            )}
          </div>
        )}
      </div>

      {/* The count is IN the control, not just in the strip above it: an operator who never reads
          the header still cannot end up unaware that thirteen aircraft exist below this line.
          Left enabled while `disabled` locks the rows — revealing them changes nothing about the
          mission, and being unable to READ the fleet while a save is in flight helps no one. */}
      {hideableCount > 0 && (
        <Button
          type="button" variant="ghost" size="sm"
          aria-pressed={revealNoGo}
          onClick={() => setRevealNoGo((shown) => !shown)}
          className="h-6 w-full justify-start px-1.5 text-[11px] uppercase tracking-wider text-muted-foreground hover:bg-accent hover:text-foreground"
        >
          {revealNoGo
            ? <EyeOff className="size-3 mr-1.5" strokeWidth={1.5} aria-hidden="true" />
            : <Eye className="size-3 mr-1.5" strokeWidth={1.5} aria-hidden="true" />}
          {revealNoGo ? `Hide ${hideableCount} NO-GO aircraft` : `Show ${hideableCount} NO-GO aircraft`}
        </Button>
      )}

      {/* Chosen-but-NO-GO must not be something the operator has to scroll back up to notice. */}
      {selectedRow !== null && selectedRow.verdict === 'no_go' && (
        <p className="text-[11px] text-status-amber-fg">
          {noGoNote(selectedRow.callsign)}
        </p>
      )}
    </fieldset>
  );
}

/**
 * One selectable row.
 *
 * A native radio rather than a `div role="radio"`: the browser then owns roving tabindex, arrow-key
 * movement within the group, and the checked state, none of which a hand-rolled version gets right
 * for free. It is `sr-only` rather than `hidden` so it stays focusable, and the focus ring is
 * painted on the visible row through `peer-focus-visible`.
 *
 * Never `disabled` — a NO-GO aircraft on screen is dimmed, not withheld. The rail hides NO-GO
 * rows by default, but a revealed one is as selectable as any other: the verdict describes now,
 * and the mission is a plan for later.
 *
 * THE LABEL IS `relative`, AND THAT IS NOT DECORATION. `sr-only` is `position: absolute`, so the
 * radio is laid out against its nearest POSITIONED ancestor. With the label static, and neither
 * this rail's scroller nor the panel body positioned, that ancestor was the compose panel's own
 * outer box — two scrollers up. Two things followed, both bad:
 *   - the radio did not move when this rail scrolled, so a row the operator could see had its
 *     input parked somewhere else entirely;
 *   - being outside both scrollers' containing-block chain, it was clipped by neither, so the
 *     panel reported ~1230px of overflow it could not show. Clicking a row focused the radio,
 *     the browser scrolled the panel to chase it, and the panel was `overflow: hidden` — a
 *     scroll container with no scrollbar and no wheel response. The form went off screen and
 *     nothing the operator could touch brought it back.
 * Positioning the radio against its own row keeps it where it is drawn, and inside the scroller
 * that is meant to clip it.
 */
function RailOption({ name, checked, onSelect, ariaLabel, dimmed = false, disabled = false, children }: {
  name: string;
  checked: boolean;
  onSelect: () => void;
  ariaLabel: string;
  dimmed?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className={disabled ? 'relative block cursor-not-allowed' : 'relative block cursor-pointer'}>
      <input
        type="radio"
        disabled={disabled}
        name={name}
        className="sr-only peer"
        checked={checked}
        aria-label={ariaLabel}
        onChange={onSelect}
      />
      <div
        className={[
          'rounded border px-2 py-1.5 min-w-0',
          // `row-hover` only while the row is not already carrying its own fill: its rule
          // (`.row-hover:hover`, specificity 0-2-0) beats a utility class, so it repainted both
          // the selected row and the dimmed one out from under them — a NO-GO row visibly
          // UN-dimmed under the cursor, which reads as "this one is fine".
          checked || dimmed ? '' : 'row-hover',
          'peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-1 peer-focus-visible:outline-ring',
          // A TINT, not the solid `--accent` (#3B82F6). Paired with nothing, that blue put the
          // row's own tokens at 1.09:1 for the model and link age, 1.6:1 for battery and 1.7:1
          // for the NO-GO reasons — so the aircraft the operator had just chosen became the
          // least readable row on screen, at the moment they most needed to read it. `select.tsx`
          // pairs `bg-accent` with `text-accent-foreground`; this rail has children on status
          // tokens that cannot be recoloured, so the fill has to stay out of their way.
          checked ? 'border-ring bg-primary/15' : 'border-border',
          // A NO-GO row recedes by BACKGROUND, never by opacity — `opacity-60` faded exactly what
          // an operator needs to read (reasons 8.91:1 → 3.85:1, chip 5.98:1 → 2.80:1, battery and
          // link 4.81:1 → 2.47:1), leaving the callsign legible and the reason it cannot fly not.
          //
          // DOWNWARD, though. `--muted` (#27272A) is LIGHTER than `--card` (#0F0F12), so the
          // first version of this fix made a NO-GO row brighter than the GO rows around it — on a
          // dark UI that reads as emphasis, the opposite of receding — and left the two
          // `text-muted-foreground` lines at ~4.05:1 inside the panel's glass card. Sinking the
          // row raises every ratio instead of lowering it.
          dimmed && !checked ? 'bg-black/25' : '',
        ].join(' ')}
      >
        {children}
      </div>
    </label>
  );
}

/** Callsign + model, the verdict chip, then the two numbers that decide it. */
function RailRowBody({ row, minBattery }: { row: RailRow; minBattery: number }) {
  const tone = batteryTone(row.batteryPercent, minBattery);
  const verdictClass = row.verdict === 'go'
    ? 'text-status-green-fg border-status-green-fg/40 bg-status-green-fg/10'
    : row.verdict === 'no_go'
      ? 'text-status-red-fg border-status-red-fg/40 bg-status-red-fg/10'
      : 'text-status-gray-fg border-border bg-transparent';

  return (
    <div className="space-y-1 min-w-0">
      <div className="flex items-baseline gap-2 min-w-0">
        <span className="text-xs font-semibold uppercase tracking-wider text-foreground truncate">
          {row.callsign}
        </span>
        <span
          className={`ml-auto shrink-0 rounded border px-1 py-px text-[9px] uppercase tracking-wider ${verdictClass}`}
          aria-hidden="true"
        >
          {RAIL_VERDICT_LABEL[row.verdict]}
        </span>
      </div>

      <div className="text-[10px] text-muted-foreground truncate" aria-hidden="true">{row.model}</div>

      <div className="flex items-center gap-2 min-w-0" aria-hidden="true">
        <div className="h-1 w-12 shrink-0 rounded-full bg-border overflow-hidden">
          <div
            className={`h-full ${TONE_BAR[tone]}`}
            style={{ width: `${batteryBarPercent(row.batteryPercent)}%` }}
          />
        </div>
        <span className={`font-mono text-[10px] tabular-nums shrink-0 ${TONE_TEXT[tone]}`}>
          {formatBattery(row.batteryPercent)}
        </span>
        <span className="ml-auto font-mono text-[10px] tabular-nums text-muted-foreground shrink-0">
          LINK {formatLinkAge(row.telemetryAgeSeconds)}
        </span>
      </div>

      {/* Reasons stay on the row. A NO-GO the operator has to hover to understand is a NO-GO they
          will assume is a UI quirk. */}
      {row.reasons.length > 0 && (
        <ul className="space-y-0.5" aria-hidden="true">
          {row.reasons.map((reason) => (
            <li key={reason} className="text-[10px] leading-snug text-status-amber-fg">{reason}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
